/**
 * LICITARS 3.0 - Unauthorized Access Page
 * Displayed when user doesn't have permission to access a route
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert, ArrowLeft, Home } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const UnauthorizedV3: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen w-full bg-navy-800 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-700 opacity-50"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>
      {/* Content Card */}
      <Card className="w-full max-w-md glass-card border-navy-500 relative z-10 animate-fade-in">
        <CardHeader className="space-y-4 text-center pb-6">
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center">
              <ShieldAlert className="w-10 h-10 text-red-400" />
            </div>
          </div>
          {/* Title */}
          <CardTitle className="text-2xl font-bold text-white">
            Acesso Negado
          </CardTitle>
          <CardDescription className="text-platinum-400 text-base">
            Você não tem permissão para acessar esta página
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Info */}
          {user && (
            <div className="glass-card border-navy-500 p-4 rounded-lg">
              <p className="text-sm text-platinum-400 mb-1">Usuário atual:</p>
              <p className="text-white font-medium">{user.name || user.username}</p>
              <p className="text-sm text-platinum-500 mt-1">
                Perfil:{" "}
                <span className="text-steel-400 capitalize">
                  {user.role === "master"
                    ? "Master"
                    : user.role === "admin"
                      ? "Administrador"
                      : user.role === "gestor"
                        ? "Gestor"
                        : "Operador"}
                </span>
              </p>
            </div>
          )}
          {/* Message */}
          <div className="text-center space-y-2">
            <p className="text-sm text-platinum-400">
              Esta funcionalidade está disponível apenas para usuários com permissões específicas.
            </p>
            <p className="text-sm text-platinum-500">
              Entre em contato com o administrador se você acredita que deveria ter acesso.
            </p>
          </div>
          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={handleGoHome}
              className="w-full gradient-navy-blue text-white font-semibold shadow-blue-glow hover:shadow-lg transition-all duration-200"
            >
              <Home className="mr-2 h-4 w-4" />
              Ir para Dashboard
            </Button>
            
            <Button
              onClick={handleGoBack}
              variant="outline"
              className="w-full glass-card border-navy-500 text-platinum-300 hover:bg-navy-700 hover:text-white transition-all duration-200"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center z-10">
        <p className="text-xs text-platinum-500">
          Se precisar de ajuda, entre em contato com o suporte
        </p>
      </div>
    </div>
  );
};

export default UnauthorizedV3;
